import {useQuery} from '@apollo/client';
import {Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import {getTabParam, setTabParam} from 'lib/urlTabParam';
import ModelHubRunsTab from 'model-hub/runs/ModelHubRunsTab';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import * as Colors from 'ui-library/Colors';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import SectionTabs from 'ui-library/SectionTabs';

import type {GetScientificModels} from './__generated/GetScientificModels';
import {ScientificModelCard} from './ScientificModelCard';
import {GET_SCIENTIFIC_MODELS_QUERY} from './ScientificModelsQueries';

export const MODEL_HUB_TAB = {
  MODELS: 'models',
  MODEL_RUNS: 'model-runs',
} as const;

type ModelHubTab = (typeof MODEL_HUB_TAB)[keyof typeof MODEL_HUB_TAB];

function isValidTab(tab: string | null): tab is ModelHubTab {
  return tab === MODEL_HUB_TAB.MODELS || tab === MODEL_HUB_TAB.MODEL_RUNS;
}

function getInitialTab(): ModelHubTab {
  const urlTab = getTabParam();
  if (urlTab === MODEL_HUB_TAB.MODEL_RUNS && !BenchlingConfig.MODEL_HUB_ENABLE_RUNS_TAB) {
    return MODEL_HUB_TAB.MODELS;
  }
  return isValidTab(urlTab) ? urlTab : MODEL_HUB_TAB.MODELS;
}

export function ScientificModelsView(): JSX.Element {
  const [activeTab, setActiveTab] = React.useState<ModelHubTab>(getInitialTab);

  const handleTabChange = useCallback((tab: ModelHubTab) => {
    setActiveTab(tab);
  }, []);

  const {data, loading, error} = useQuery<GetScientificModels>(GET_SCIENTIFIC_MODELS_QUERY);

  // Derive the URL ?tab= param from React state so they can't get out of sync.
  useEffect(() => {
    setTabParam(activeTab);
  }, [activeTab]);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_HUB_VIEWED);
  }, []);

  const renderModelsContent = (): JSX.Element => {
    if (loading) {
      return <Text>Loading models...</Text>;
    }

    if (error) {
      return <Text color="red">Error loading models: {error.message}</Text>;
    }

    const models = data?.scientificModels ?? [];

    return (
      <div className={css(styles.modelsGrid)}>
        {models.map((model) => (
          <ScientificModelCard
            key={model.systemName}
            systemName={model.systemName}
            displayName={model.displayName}
            iconPath={model.iconPath}
            description={model.description}
            paperLink={model.paperLink}
            inputTypes={model.inputTypes}
            category={model.category}
            onNavigateToRuns={
              BenchlingConfig.MODEL_HUB_ENABLE_RUNS_TAB
                ? () => {
                    handleTabChange(MODEL_HUB_TAB.MODEL_RUNS);
                  }
                : undefined
            }
          />
        ))}
      </div>
    );
  };

  const tabs = [
    {
      id: 'model-hub-models-tab',
      ariaControls: 'model-hub-models-panel',
      text: 'Models',
      isActive: activeTab === MODEL_HUB_TAB.MODELS,
      handleClick: () => {
        setActiveTab(MODEL_HUB_TAB.MODELS);
      },
    },
    ...(BenchlingConfig.MODEL_HUB_ENABLE_RUNS_TAB
      ? [
          {
            id: 'model-hub-runs-tab',
            ariaControls: 'model-hub-runs-panel',
            text: 'Model Runs',
            isActive: activeTab === MODEL_HUB_TAB.MODEL_RUNS,
            handleClick: () => {
              setActiveTab(MODEL_HUB_TAB.MODEL_RUNS);
            },
          },
        ]
      : []),
  ];

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.titleRow)}>
        <SplitBreadcrumbs
          crumbs={[<CrumbLink key="root" text="Model Hub" />]}
          trailingCrumbIcon={
            <span className={css(styles.previewBadge)}>
              <PreviewBadge />
            </span>
          }
        />
        <ClickableIcon
          altText="Close"
          className={css(styles.closeButton)}
          icon="panel-hidden"
          iconStyle={ClickableIconStyle.NEGATIVE}
          onClick={() => navigateToURL('/editor')}
        />
      </div>
      <div className={css(styles.sectionTabs)}>
        <SectionTabs tabs={tabs} />
      </div>
      {activeTab === MODEL_HUB_TAB.MODELS && (
        <div
          id="model-hub-models-panel"
          role="tabpanel"
          aria-labelledby="model-hub-models-tab"
          className={css(styles.tabPanel)}
        >
          {renderModelsContent()}
        </div>
      )}
      {BenchlingConfig.MODEL_HUB_ENABLE_RUNS_TAB && activeTab === MODEL_HUB_TAB.MODEL_RUNS && (
        <div
          id="model-hub-runs-panel"
          role="tabpanel"
          aria-labelledby="model-hub-runs-tab"
          className={css(styles.tabPanel)}
        >
          <ModelHubRunsTab
            onNavigateToModels={() => {
              setActiveTab(MODEL_HUB_TAB.MODELS);
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: '16px 24px 0 90px',
    width: '100%',
    maxWidth: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  closeButton: {
    margin: 10,
  },
  previewBadge: {
    marginLeft: 8,
  },
  sectionTabs: {
    borderBottomColor: Colors.GRAY,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
  },
  tabPanel: {
    marginTop: 12,
  },
  modelsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    width: '100%',
  },
});
